(() => {
  const TARGET_SELECTOR = '[data-element-id="textarea-1"] textarea';
  const MAX_COLS = 10;
  const MAX_ROWS = 5;
  const MAX_CHARS = MAX_COLS * MAX_ROWS + (MAX_ROWS - 1);
  const MAX_LENGTH_WITH_BREAKS = MAX_CHARS;

  // 改行コードを統一、10文字×5行までに整形
  function normalizeLineBreaks(value) {
    return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  }

  function formatTextareaValue(value) {
    const normalized = normalizeLineBreaks(value);

    let result = '';
    let currentRow = 1;
    let currentCol = 0;
    let visibleCount = 0;

    for (const char of normalized) {
      if (char === '\n') {
        if (currentRow >= MAX_ROWS) {
          break;
        }

        result += '\n';
        currentRow += 1;
        currentCol = 0;
        continue;
      }

      if (visibleCount >= MAX_CHARS) {
        break;
      }

      if (currentCol >= MAX_COLS) {
        if (currentRow >= MAX_ROWS) {
          break;
        }

        result += '\n';
        currentRow += 1;
        currentCol = 0;
      }

      result += char;
      currentCol += 1;
      visibleCount += 1;
    }

    return result;
  }

  function countCharacters(value) {
    return normalizeLineBreaks(value).replace(/\n/g, '').length;
  }

  // カウンターと補助文言を更新する
  function updateCounter(textarea) {
    const element = textarea.closest('.gpo-element');
    if (!element) return;

    const counter = element.querySelector('.gpo-counter');
    if (!counter) return;

    const currentLength = countCharacters(textarea.value);
    counter.textContent = `${currentLength}/${MAX_CHARS}`;
  }

  function updateHelpText(textarea) {
    const element = textarea.closest('.gpo-element');
    if (!element) return;

    const helpText = element.querySelector('.gpo-helptext');
    if (!helpText) return;

    if (helpText.textContent.trim() !== '') {
      return;
    }

    helpText.textContent = ``;
  }

  function syncPreview(textarea) {
    textarea.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // 整形後のカーソル位置を補正
  function restoreCaret(textarea, oldValue, newValue, oldPosition) {
    if (document.activeElement !== textarea) {
      return;
    }

    const oldPrefix = oldValue.slice(0, oldPosition);
    const formattedPrefix = formatTextareaValue(oldPrefix);
    const nextPosition = Math.min(formattedPrefix.length, newValue.length);

    try {
      textarea.setSelectionRange(nextPosition, nextPosition);
    } catch (error) {}
  }

  // IME確定後、変換部分だけ改行を除去して再構築
  function rebuildAfterComposition(textarea, snapshot) {
    const currentValue = normalizeLineBreaks(textarea.value);
    const before = snapshot.value.slice(0, snapshot.start);
    const after = snapshot.value.slice(snapshot.end);

    if (
      !currentValue.startsWith(before) ||
      !currentValue.endsWith(after) ||
      currentValue.length < before.length + after.length
    ) {
      return formatTextareaValue(currentValue);
    }

    const inserted = currentValue.slice(
      before.length,
      currentValue.length - after.length
    );

    const cleanedInserted = inserted.replace(/\n/g, '');
    const rebuiltValue = before + cleanedInserted + after;

    return formatTextareaValue(rebuiltValue);
  }

  // textarea に入力制御を適用
  function bindTextarea(textarea) {
    if (!textarea || textarea.dataset.awFormatted === 'true') {
      return;
    }

    textarea.dataset.awFormatted = 'true';
    textarea.setAttribute('rows', String(MAX_ROWS));
    textarea.setAttribute('wrap', 'soft');

    textarea.setAttribute('maxlength', String(MAX_LENGTH_WITH_BREAKS));
    textarea.setAttribute('data-maxlength', String(MAX_CHARS));

    let isComposing = false;
    let compositionSnapshot = null;

    function applyFormatting() {
      if (isComposing) {
        return;
      }

      const oldValue = textarea.value;
      const oldPosition = textarea.selectionStart ?? oldValue.length;
      const formattedValue = formatTextareaValue(oldValue);

      if (oldValue !== formattedValue) {
        textarea.value = formattedValue;
        restoreCaret(textarea, oldValue, formattedValue, oldPosition);
        syncPreview(textarea);
      }

      updateCounter(textarea);
      updateHelpText(textarea);
    }

    textarea.addEventListener('compositionstart', () => {
      isComposing = true;
      compositionSnapshot = {
        value: normalizeLineBreaks(textarea.value),
        start: textarea.selectionStart ?? textarea.value.length,
        end: textarea.selectionEnd ?? textarea.value.length
      };
    });

    textarea.addEventListener('compositionend', () => {
      isComposing = false;

      requestAnimationFrame(() => {
        const oldValue = textarea.value;
        const oldPosition = textarea.selectionStart ?? oldValue.length;

        const formattedValue = compositionSnapshot
          ? rebuildAfterComposition(textarea, compositionSnapshot)
          : formatTextareaValue(oldValue);

        compositionSnapshot = null;

        if (oldValue !== formattedValue) {
          textarea.value = formattedValue;
          restoreCaret(textarea, oldValue, formattedValue, oldPosition);
          syncPreview(textarea);
        }

        updateCounter(textarea);
        updateHelpText(textarea);
      });
    });

    textarea.addEventListener('input', () => {
      if (isComposing) {
        return;
      }

      applyFormatting();
    });

    textarea.addEventListener('paste', () => {
      requestAnimationFrame(applyFormatting);
    });

    textarea.addEventListener('blur', () => {
      isComposing = false;
      compositionSnapshot = null;
      applyFormatting();
    });

    applyFormatting();
  }

  // 初期表示と後描画の textarea に適用
  function setup() {
    document.querySelectorAll(TARGET_SELECTOR).forEach(bindTextarea);
  }

  const observer = new MutationObserver(setup);

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();